import AutosaveSuccess from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Divider } from "@mui/material";
import get from "lodash/get";
import { useEffect } from "react";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import Comments from "../comments/CommentsContainer";
import comments_constants from "../comments/constants";
import comments_operations from "../comments/ducks/operations";
import DocumentDrop from "../documents/beta/DocumentDropContainerBeta";
import documentTypes from "../documents/documentTypes";
import utils from "../utils";
import constants from "./constants";
import operations from "./ducks/operations";
import Property from "./Property";
import "./Property.css";
import PropertyToolbar from "./PropertyToolbar";

const PropertyContainer = (props) => {
    const {
        clearPropertyState,
        determineDocumentOptions,
        disableInputs,
        errors,
        getProperty,
        history,
        match,
        property,
    } = props;
    const { id } = match.params;
    useEffect(() => {
        if (id) {
            handleGetProperty();
        }
        ReactGA.send({ hitType: "pageview", page: "/property/*", title: `Property Summary: ${id}` });
        return () => {
            clearPropertyState();
        };
    }, []);

    const handleGetProperty = async () => {
        const getPropertyFailed = await getProperty(id);
        if (getPropertyFailed) {
            history.push("/properties");
        }
    };
    return (
        <div style={{ minWidth: "800px" }}>
            <PropertyToolbar {...props} />
            <div className="propertyFlexField">
                <DocumentDrop
                    entityKey={id}
                    type="property"
                    options={determineDocumentOptions}
                    documentTypes="propertyForm"
                    formDisabled={disableInputs}
                    parentType={"PROPERTY"}
                    stateCode={get(property, "primaryAddress.city.state.stateCode", null)}
                />
            </div>
            <div className="propertyContainer">
                <div className="propertyFlexField">
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="expansionPanelContentContainer">
                                <AutosaveSuccess className="waterloo" style={{ paddingTop: "4px" }} />
                                <div>
                                    Property Information
                                    <div>
                                        <small className="entityIDText">Property ID: {property.propertyKey}</small>
                                    </div>
                                </div>
                            </div>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails>
                            <Property {...props} errors={get(errors, "property", {})} />
                        </AccordionDetails>
                    </Accordion>
                </div>
                <div>
                    <Comments parentRecordType={comments_constants.PROPERTY_PARENT_RECORD_TYPE} parentRecordId={id} />
                </div>
            </div>
        </div>
    );
};

const determineDocumentOptions = (role, property) => {
    const allDocTypes = documentTypes.allDocTypesList;
    const isCDTOrAdmin = ["dataEntry", "dataAdmin"].includes(role);
    const isPropertyManagement = ["propertyManagement"].includes(role);

    let options = {
        canView: [...allDocTypes],
        canDelete: [],
        canUpload: [],
    };

    if (["ACTIVE", "ACTIVE_UPDATE_REQUESTED"].includes(property?.propertyAttributes?.propertyStatus)) {
        if (isPropertyManagement) options.canUpload = [...allDocTypes];
    } else if (isCDTOrAdmin) {
        options.canUpload = [...allDocTypes];
        options.canDelete = [...allDocTypes];
    }

    return options;
};

const mapStateToProps = (state) => {
    return {
        userProfile: state.authReducer.userProfile,
        property: get(state, "propertyReducer.property", false),
        propertyType: get(state.propertyReducer, "property.propertyType", null),
        attributes: get(state.propertyReducer, "property.propertyAttributes", {}),
        parcels: get(state.propertyReducer, "property.propertyAttributes.parcels", []),
        errors: state.utilsReducer.errors ? state.utilsReducer.errors : {},
        searchResults: state.propertyReducer.searchResults,
        isSaving: state.propertyReducer.isSaving ? state.propertyReducer.isSaving : null,
        saveError: state.propertyReducer.saveError ? state.propertyReducer.saveError : null,
        lastSaved: state.propertyReducer.lastSaved ? state.propertyReducer.lastSaved : null,
        numComments: state.commentsReducer.comments.length,
        snackbar: state.utilsReducer.snackbar,
        confirmDialog: state.utilsReducer.confirmDialog,
        disableInputs: get(
            constants.disabledStatuses,
            `${state.authReducer.userProfile.role}.${get(
                state.propertyReducer,
                "property.propertyAttributes.propertyStatus"
            )}`,
            true
        ),
        elementsSaving: state.utilsReducer.elementsSaving,
        numResults: state.propertyReducer.numResults,
        displayDeleteDialog: state.propertyReducer.displayDeleteDialog,
        displayNoDeleteDialog: state.propertyReducer.displayNoDeleteDialog,
        determineDocumentOptions: determineDocumentOptions(
            state.authReducer.userProfile.role,
            state.propertyReducer.property
        ),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getProperty: (propertyKey) => {
            dispatch(operations.getProperty(propertyKey));
        },
        handleSave: () => {
            dispatch(operations.handleSave());
        },
        handleSaveAndExit: () => {
            return dispatch(operations.handleSaveAndExit());
        },
        handleSubmit: (save, callback) => {
            dispatch(utils.operations.toggleConfirm());
            // if (save)
            dispatch(operations.handleSubmitPropertyForm(save, callback));
        },
        handleApprovePropertyRequestAsDataEntry: (save, callback) => {
            dispatch(utils.operations.toggleConfirm());
            return dispatch(operations.handleApprovePropertyRequestAsDataEntry(save, callback));
        },
        handleInputChange: (field, value) => {
            dispatch(operations.handleInputChange(field, value));
        },

        toggleSnackBar: (open, variant, message) => {
            dispatch(utils.operations.snackbar(open, variant, message));
        },
        toggleConfirm: (onSubmit) => {
            dispatch(utils.operations.toggleConfirm(onSubmit));
        },
        handleDocumentWarning: (open, proceed, docs) => {
            dispatch(operations.handleDocumentWarning(open, proceed, docs));
        },
        handleConfirmWarning: () => {
            dispatch(operations.handleDocumentWarning(false, true, []));
        },
        toggleComments: () => {
            dispatch(comments_operations.toggleComments());
        },
        handleAddArrayElement: (arrayPath) => {
            dispatch(operations.addArrayElement(arrayPath));
        },
        handleDeleteArrayElement: (arrayPath, index) => {
            dispatch(operations.deleteArrayElement(arrayPath, index));
        },
        handleUpdateArrayElement: (arrayPath, index, field, value) => {
            dispatch(operations.handleInputChange(arrayPath + "[" + index + "]." + field, value));
        },
        clearPropertyState: () => {
            dispatch(operations.clearPropertyDetailState());
        },
        handleChooseDisplayDeleteDialog: (isOpen, propertyKey) => {
            dispatch(operations.displayDeleteDialog(isOpen, propertyKey));
        },
        handleDeleteProperty: (propertyKey) => {
            return dispatch(operations.handleDeleteProperty(propertyKey));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyContainer);
